import React, { useMemo, useState } from "react";
import { FiCheck, FiCheckCircle, FiCode, FiCopy, FiFileText, FiShield, FiX } from "react-icons/fi";
import StatusBadge from "./StatusBadge";
import MarkdownRenderer from "./MarkdownRenderer";
import { AIStarIcon } from "../../../helper/HwIcon";
import { getVendorIcon } from "./ErrorTable";
import api from "../../../helper/errorMonitorApi";

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj ?? {}, null, 2);
  } catch (e) {
    return String(obj);
  }
};

const ErrorDetailsModal = ({
  open,
  item,
  mode = "details",
  onClose,
  solution,
  solutionLoading,
  onResolved,
  resolving = false,
  error,
  handleGenerate
}) => {
  const [generateCode, setGenerateCode] = useState(false);
  const [copy, setCopied] = useState(false);
  const [generatedFix, setGeneratedFix] = useState(null);
  const jsonText = useMemo(() => safeStringify(item?.errorJson), [item]);
  const stackText = item?.errorJson?.lastErrorStack || "";

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedFix?.fullFixedSnippet || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  const generateCodeFn = async (item, force) => {
    setGenerateCode(true);
    setGeneratedFix(null);
    if (!item?.id) return;
    try {
      const resp = await api.post("/generate-code", { logId: item.id, force });
      console.log(resp.data)
      if (resp.data?.status === "success") {
        console.log(resp.data.data.content)
        setGeneratedFix(resp.data.data.content);
      }
      return true;
    } catch (e) {
      console.error("Failed to generate code", e);
      return false;
    } finally {
      setGenerateCode(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="hw-modal fade show">
        <div className="hw-modal-dialog hw-modal-dialog-centered hw-modal-dialog-scrollable">
          <div className="hw-modal-content" style={{ borderRadius: 14, overflow: "hidden" }}>
            <div className="hw-modal-header" style={{ background: "var(--em-bg)" }}>
              <div className="hw-modal-headerTitle">
                <span className="em-modalTitle">
                  {mode === "solution" ? <FiShield /> : <FiFileText />}
                  {mode === "solution" ? "AI Suggested Solution" : "Error Details"}
                </span>
              </div>
              <button type="button" className="hw-modal--close" onClick={onClose}>
                <FiX />
              </button>
            </div>

            <div className="hw-modal-body">
              <div className="em-panel" style={{ boxShadow: "none", padding: 12 }}>
                <div className="d-flex justify-between align-items-center" style={{ gap: 12 }}>
                  <div>
                    <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em" }}>
                      <span className="em-text-primary">{getVendorIcon(item.module)} {item?.vendor}</span> • {item?.module}
                    </div>
                    <div style={{ fontWeight: 800, fontSize: 18, color: "var(--em-text)" }}>
                      {item?.errorMessage}
                    </div>
                    <div className="em-muted em-bold" style={{ marginTop: 4 }}>
                      Endpoint: <code>{String(item?.endpoint).length > 25 ? `${String(item?.endpoint).slice(0, 25)}...` : item?.endpoint}</code>
                      {item?.functionName ? (
                        <>
                          {" "}
                          •  Function: <code>{item.functionName}</code>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="d-flex" style={{ gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <StatusBadge value={item?.severity} type="severity" />
                    <div className="em-btn">
                      Occurrence Count : {item?.occurrenceCount}
                    </div>
                  </div>
                </div>
              </div>

              {mode === "solution" ? (
                <div className="em-panel" style={{ marginTop: 12 }}>
                  {solutionLoading ? (
                    <>
                      <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em", marginBottom: 6 }}>
                        GENERATING SOLUTION
                      </div>
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <span className="em-spinner" />
                        <span className="em-muted em-bold">Please wait while we analyze this error...</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em", marginBottom: 6 }}>
                        RECOMMENDATION
                      </div>
                      <MarkdownRenderer
                        content={
                          solution?.answer || "No suggestion available for this error."
                        }
                      />
                      {solution?.codeSnippet && solution.codeSnippet.trim() ? (
                        <div style={{ marginTop: 12 }}>
                          <div
                            className="em-muted em-bold"
                            style={{ fontSize: 12, letterSpacing: ".08em", marginBottom: 6 }}
                          >
                            CODE SNIPPET
                          </div>
                          <pre
                            className="em-json"
                            style={{
                              marginTop: 4,
                              background: "rgba(15, 23, 42, 0.04)",
                              border: "1px solid var(--em-border)",
                              borderRadius: 12,
                              padding: 12,
                              overflow: "auto",
                              maxHeight: 220,
                              fontSize: 12,
                              lineHeight: 1.6,
                            }}
                          >
                            {solution.codeSnippet}
                          </pre>
                        </div>
                      ) : null}
                    </>
                  )}
                  <div className="d-flex hw-gapy10">
                    {!solutionLoading && <button
                      type="button"
                      className="em-btn mt-10 em-btn--primary"
                      onClick={() => handleGenerate(item, true)}
                      disabled={solutionLoading}
                    >
                      {/* {resolving ? "Regenrating..." : "Regenrate"} */}
                      <AIStarIcon /> Regenrate
                    </button>}
                    {!solutionLoading && (
                      <button
                        type="button"
                        className="em-btn mt-10 em-btn--primary"
                        onClick={() => generateCodeFn(item, false)}
                        disabled={generateCode}
                      >
                        {generateCode ? (
                          <span className="em-spinner" style={{ width: 14, height: 14, marginRight: 8 }} />
                        ) : (
                          <AIStarIcon />
                        )}{" "}
                        {generateCode ? "Generating..." : "Generate Code"}
                      </button>
                    )}
                  </div>
                  {generatedFix && (
                    <div className="em-panel generated-fix-panel">
                      <div className="d-flex align-items-center mb-3" style={{ gap: 8, color: "#059669" }}>
                        <AIStarIcon />
                        <span className="em-text-primary">AI Generated Fix</span>
                      </div>

                      <div className="mb-3">
                        <div className="em-bold em-text-primary">ISSUE IDENTIFIED</div>
                        <div className="em-muted">{generatedFix.issue}</div>
                      </div>

                      <div className="mb-3">
                        <div className="em-text-primary em-bold">FIX SUMMARY</div>
                        <div className="em-muted">{generatedFix.fixSummary}</div>
                      </div>

                      <div className="fix-diff-container" style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #d1fae5", marginBottom: 16 }}>
                        <div style={{ background: "#fee2e2", padding: "8px 12px", borderBottom: "1px solid #fecaca" }}>
                          <div className="em-text-primary em-bold" style={{ fontSize: 10, color: "#991b1b" }}>BEFORE (Line {generatedFix.lineToChange})</div>
                          <code style={{ color: "#991b1b", fontSize: 12 }}>- {generatedFix.before}</code>
                        </div>
                        <div style={{ background: "#d1fae5", padding: "8px 12px" }}>
                          <div className="em-text-primary em-bold" style={{ fontSize: 10, color: "#065f46" }}>AFTER</div>
                          <code style={{ color: "#065f46", fontSize: 12 }}>+ {generatedFix.after}</code>
                        </div>
                      </div>

                      {generatedFix.fullFixedSnippet && (
                        <div className="mb-3  mt-10">
                          <div className="d-flex justify-between">
                            <div className="em-bold em-text-primary">FULL FIXED SNIPPET</div>
                            <button
                              onClick={handleCopy}
                              className="em-btn"
                            >
                              {copy ? <FiCheck /> : <FiCopy />}
                              {copy ? "Copied" : "Copy"}
                            </button>
                          </div>
                          <pre style={{
                            background: "#0f172a",
                            color: "#e2e8f0",
                            padding: 12,
                            borderRadius: 8,
                            fontSize: 12,
                            lineHeight: 1.5,
                            overflowX: "auto",
                            border: "1px solid #1e293b"
                          }}>
                            {generatedFix.fullFixedSnippet}
                          </pre>
                        </div>
                      )}

                      {generatedFix.notes && (
                        <div style={{ fontSize: 12, fontStyle: "italic", color: "#047857", borderTop: "1px solid #d1fae5", paddingTop: 8 }}>
                          <strong>Note:</strong> {generatedFix.notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {item?.detailsMessage ? (
                    <div className="em-panel" style={{ marginTop: 12 }}>
                      <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em", marginBottom: 6 }}>
                        DETAILED MESSAGE
                      </div>
                      <div
                        style={{ color: "var(--em-text)", lineHeight: 1.7 }}
                        dangerouslySetInnerHTML={{ __html: item.detailsMessage }}
                      />
                      <div className="col-md-4">
                        <div className="em-muted em-bold">Actual Linenumber</div>
                        <div><code>{item?.actualLine ?? item?.lineNumber ?? "-"}</code></div>
                      </div>
                      <div className="col-md-4">
                        <div className="em-muted em-bold">Actual File</div>
                        <div><code>{item?.actualFile ?? item?.fileName ?? "-"}</code></div>
                      </div>
                    </div>
                  ) : null}

                  {(item?.errorJson?.httpStatus ||
                    item?.errorJson?.requestMethod ||
                    item?.errorJson?.requestPath ||
                    item?.errorJson?.fileName ||
                    item?.errorJson?.lineNumber) ? (
                    <div className="em-panel" style={{ marginTop: 12 }}>
                      <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em", marginBottom: 6 }}>
                        REQUEST / LOCATION
                      </div>
                      <div className="row hw-gapx15 hw-gapy10">
                        <div className="col-md-4">
                          <div className="em-muted em-bold">HTTP Status</div>
                          <div><strong>{item?.errorJson?.httpStatus ?? "-"}</strong></div>
                        </div>
                        <div className="col-md-4">
                          <div className="em-muted em-bold">Method</div>
                          <div><code>{item?.errorJson?.requestMethod ?? "-"}</code></div>
                        </div>
                        <div className="col-md-4">
                          <div className="em-muted em-bold">Path</div>
                          <div><code>{item?.errorJson?.requestPath ?? item?.endpoint ?? "-"}</code></div>
                        </div>
                        <div className="col-md-8">
                          <div className="em-muted em-bold">File</div>
                          <div style={{ wordBreak: "break-word" }}>
                            <code>{item?.errorJson?.fileName ?? "-"}</code>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="em-muted em-bold">Line</div>
                          <div><strong>{item?.errorJson?.lineNumber ?? "-"}</strong></div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {Array.isArray(item?.errorJson?.tags) && item.errorJson.tags.length ? (
                    <div className="em-panel" style={{ marginTop: 12 }}>
                      <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em", marginBottom: 6 }}>
                        TAGS
                      </div>
                      <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                        {item.errorJson.tags.map((t) => (
                          <span key={t} className="em-badge em-badge--status">{t}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {stackText ? (
                    <div className="em-panel" style={{ marginTop: 12 }}>
                      <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em", marginBottom: 6 }}>
                        STACK TRACE
                      </div>
                      <pre
                        className="em-json"
                        style={{
                          marginTop: 10,
                          background: "rgba(15, 23, 42, 0.04)",
                          border: "1px solid var(--em-border)",
                          borderRadius: 12,
                          padding: 12,
                          overflow: "auto",
                          maxHeight: 220,
                          fontSize: 12,
                          lineHeight: 1.6,
                        }}
                      >
                        {stackText}
                      </pre>
                    </div>
                  ) : null}

                  <div className="em-panel" style={{ marginTop: 12 }}>
                    <div className="d-flex justify-between align-items-center" style={{ gap: 10 }}>
                      <div className="em-muted em-bold" style={{ fontSize: 12, letterSpacing: ".08em" }}>
                        ERROR JSON
                      </div>
                      <span className="em-muted em-bold" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <FiCode /> payload
                      </span>
                    </div>
                    <pre
                      className="em-json"
                      style={{
                        marginTop: 10,
                        background: "rgba(15, 23, 42, 0.04)",
                        border: "1px solid var(--em-border)",
                        borderRadius: 12,
                        padding: 12,
                        overflow: "auto",
                        maxHeight: 260,
                        fontSize: 12,
                        lineHeight: 1.6,
                      }}
                    >
                      {jsonText}
                    </pre>
                  </div>
                  <div className="d-flex" style={{ gap: 10 }}>
                    {item?.status !== "Resolved" && (
                      <button
                        type="button"
                        className="em-btn em-btn--primary mt-10"
                        onClick={() => onResolved(item.id)}
                        disabled={resolving}
                      >
                        {resolving ? "Marking..." : <>{<FiCheckCircle />} Mark as Resolved</>}
                      </button>
                    )}
                    <button
                      type="button"
                      className="em-btn em-btn--primary mt-10"
                      onClick={() => generateCodeFn(item, true)}
                      disabled={generateCode}
                    >
                      {generateCode ? (
                        <span className="em-spinner" style={{ width: 14, height: 14, marginRight: 8 }} />
                      ) : (
                        <AIStarIcon />
                      )}{" "}
                      {generateCode ? "Generating..." : "Generate Code"}
                    </button>
                  </div>
                  {error && <div className="hw-p-error-cls mb-3 mt-10">{error}</div>}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="hw-modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default ErrorDetailsModal;

